import React, { useState } from 'react';
import { FiltrosAnuncio as FiltrosType, CATEGORIAS, ESTADOS_BRASIL, Categoria } from '../../types';
import Input from '../common/Input';
import Button from '../common/Button';

interface FiltrosAnuncioProps {
  filtros: FiltrosType;
  onChange: (filtros: Partial<FiltrosType>) => void;
  onApply: () => void;
  onClear: () => void;
  isMobile?: boolean;
  onClose?: () => void;
}

const FiltrosAnuncio: React.FC<FiltrosAnuncioProps> = ({
  filtros,
  onChange,
  onApply,
  onClear,
  isMobile = false,
  onClose,
}) => {
  const [localFiltros, setLocalFiltros] = useState<FiltrosType>(filtros);
  
  const selectedCategoria = CATEGORIAS.find((c) => c.id === localFiltros.categoria);

  const handleChange = (key: keyof FiltrosType, value: any) => {
    const newFiltros = { ...localFiltros, [key]: value || undefined };
    if (key === 'categoria') {
      newFiltros.subcategoria = undefined;
    }
    setLocalFiltros(newFiltros);
    if (!isMobile) {
      onChange(newFiltros);
    }
  };

  const handleApply = () => {
    onChange(localFiltros);
    onApply();
    onClose?.();
  };

  const handleClear = () => {
    const emptyFiltros: FiltrosType = {
      ordenar: 'recentes',
      pagina: 1,
      limite: 12,
    };
    setLocalFiltros(emptyFiltros);
    onChange(emptyFiltros);
    onClear();
  };

  const formatPrice = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (!numbers) return '';
    return Number(numbers).toLocaleString('pt-BR');
  };

  const parsePrice = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers ? Number(numbers) : undefined;
  };

  return (
    <div className={`bg-white ${isMobile ? 'p-4' : 'rounded-xl border border-gray-200 p-6'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-gray-900">Filtros</h3>
        <button
          onClick={handleClear}
          className="text-sm text-green-700 hover:text-green-800 font-medium"
        >
          Limpar tudo
        </button>
      </div>

      <div className="space-y-6">
        {/* Busca */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Buscar
          </label>
          <Input
            type="text"
            placeholder="O que você procura?"
            value={localFiltros.busca || ''}
            onChange={(e) => handleChange('busca', e.target.value)}
            leftIcon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            }
          />
        </div>

        {/* Categoria */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Categoria
          </label>
          <div className="space-y-2 max-h-60 overflow-y-auto scrollbar-thin">
            {CATEGORIAS.map((categoria) => (
              <label
                key={categoria.id}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  localFiltros.categoria === categoria.id
                    ? 'bg-green-50 border-2 border-green-500'
                    : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                }`}
              >
                <input
                  type="radio"
                  name="categoria"
                  value={categoria.id}
                  checked={localFiltros.categoria === categoria.id}
                  onChange={(e) => handleChange('categoria', e.target.value as Categoria)}
                  className="sr-only"
                />
                <span className="text-xl">{categoria.icone}</span>
                <span className="font-medium text-gray-900">{categoria.nome}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Subcategoria */}
        {selectedCategoria && selectedCategoria.subcategorias.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subcategoria
            </label>
            <select
              value={localFiltros.subcategoria || ''}
              onChange={(e) => handleChange('subcategoria', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
            >
              <option value="">Todas</option>
              {selectedCategoria.subcategorias.map((sub) => (
                <option key={sub} value={sub}>{sub}</option>
              ))}
            </select>
          </div>
        )}

        {/* Faixa de Preço */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Faixa de Preço
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Input
                type="text"
                placeholder="Mínimo"
                value={localFiltros.precoMin ? formatPrice(localFiltros.precoMin.toString()) : ''}
                onChange={(e) => handleChange('precoMin', parsePrice(e.target.value))}
                leftIcon={<span className="text-gray-400">R$</span>}
              />
            </div>
            <div>
              <Input
                type="text"
                placeholder="Máximo"
                value={localFiltros.precoMax ? formatPrice(localFiltros.precoMax.toString()) : ''}
                onChange={(e) => handleChange('precoMax', parsePrice(e.target.value))}
                leftIcon={<span className="text-gray-400">R$</span>}
              />
            </div>
          </div>
        </div>

        {/* Estado */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Estado
          </label>
          <select
            value={localFiltros.estado || ''}
            onChange={(e) => handleChange('estado', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
          >
            <option value="">Todos os estados</option>
            {ESTADOS_BRASIL.map((estado) => (
              <option key={estado.sigla} value={estado.sigla}>
                {estado.nome}
              </option>
            ))}
          </select>
        </div>

        {/* Cidade */}
        {localFiltros.estado && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cidade
            </label>
            <Input
              type="text"
              placeholder="Digite a cidade"
              value={localFiltros.cidade || ''}
              onChange={(e) => handleChange('cidade', e.target.value)}
            />
          </div>
        )}

        {/* Ordenação */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ordenar por
          </label>
          <select
            value={localFiltros.ordenar || 'recentes'}
            onChange={(e) => handleChange('ordenar', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
          >
            <option value="recentes">Mais recentes</option>
            <option value="relevancia">Relevância</option>
            <option value="preco_asc">Menor preço</option>
            <option value="preco_desc">Maior preço</option>
          </select>
        </div>
      </div>

      {/* Actions - Mobile */}
      {isMobile && (
        <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
          <Button variant="outline" onClick={handleClear} fullWidth>
            Limpar
          </Button>
          <Button onClick={handleApply} fullWidth>
            Aplicar Filtros
          </Button>
        </div>
      )}
    </div>
  );
};

export default FiltrosAnuncio;
